import { useEffect, useMemo, useState } from "react";
import { FormikProvider, useFormikContext } from "formik";
import { styled } from "@mui/material";
import { dashboardText } from "pages/AgronomistDashboard/_utils/shared";
import moment from "moment";
import {
  CloseRounded,
  LandscapeOutlined,
  SearchOutlined,
  KeyboardArrowUpRounded,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useActionChoices, useStressTypes } from "stores";
import { ACTION_VIEW_MODE } from "constants/action-form";
import { ACTION_STATUS } from "constants/actions";
import {
  useActionModalGetContext,
  useActionModalSetContext,
} from "context/ActionModalProvider";
import { varietyFullName } from "utils/extractInfoFromResponses";
import StressSelectInput from "components/action-inputs/stress/StressSelectInput";
import EnemySelectInput from "components/action-inputs/enemy/EnemySelectInput";
import TaskSelectInput from "components/action-inputs/task/TaskSelectInput";
import OperationInput from "components/action-inputs/operation/OperationInput";
import OtherOperationVariant from "components/action-inputs/other-operation-variant/OtherOperationVariant";
import { FormDateInput, FormSelectInput } from "form";
import ActionDetails from "../details/ActionDetails";
import { useActionFormikSetup } from "../utils/useFormikSetup";
import ActionAlertsStrip from "./ActionAlertsStrip";
import Button from "../../ButtonCustom/ButtonNew";

/* ─────────────────────────────────────────────────────────────────── */
/* Styled: Modal chrome                                                */
/* ─────────────────────────────────────────────────────────────────── */

export const CommonActionModalWrapper = styled("div")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "1000px",
  height: "82%",
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "calc(100vh - 32px)",
  backgroundColor: "#ffffff",
  borderRadius: "18px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  outline: "none",
  boxShadow:
    "0 20px 40px rgba(13, 47, 61, 0.14), 0 4px 12px rgba(13, 47, 61, 0.08)",
  "@media (max-width:1100px)": {
    width: "940px",
  },
  "@media (max-width:920px)": {
    height: "90%",
  },
});

const ModalHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  flexShrink: 0,
  backgroundColor: "#ffffff",
});

const ModalTitle = styled("div")({
  ...dashboardText,
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "20px",
  letterSpacing: "0.1px",
  color: "#1e2b32",
});

const ModalHeaderActions = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const SubmitBtn = styled(Button)({
  ...dashboardText,
  height: "32px",
  borderRadius: "10px",
  padding: "0 16px",
  textTransform: "none",
  minWidth: "auto",
  backgroundColor: "#00485a",
  "&:hover": {
    backgroundColor: "#005469",
  },
});

const CloseBtn = styled("button")({
  background: "none",
  border: "1px solid #dde4e9",
  borderRadius: "10px",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "#536870",
  transition: "background 0.15s, border-color 0.15s",
  flexShrink: 0,
  padding: 0,
  "&:hover": {
    background: "#f4f6f8",
    borderColor: "#c8d1d7",
  },
});

const ModalBody = styled("div")({
  display: "flex",
  flexDirection: "row",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
});

/* ─────────────────────────────────────────────────────────────────── */
/* Styled: Left fields panel                                           */
/* ─────────────────────────────────────────────────────────────────── */

const FieldsPanel = styled("div")({
  flex: "0 0 22%",
  minWidth: "180px",
  maxWidth: "260px",
  marginLeft: "16px",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  overflow: "hidden",
});

const FieldsPanelHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 8px 8px",
  cursor: "pointer",
  userSelect: "none",
  flexShrink: 0,
});

const FieldsPanelTitleGroup = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const FieldsPanelIcon = styled(LandscapeOutlined)({
  fontSize: "16px",
  color: "#6e8391",
});

const FieldsPanelTitle = styled("span")({
  ...dashboardText,
  fontSize: "15px",
  lineHeight: "20px",
  color: "#002129",
});

const FieldsCountBadge = styled("span")({
  ...dashboardText,
  backgroundColor: "#356f89",
  borderRadius: "999px",
  padding: "1px 8px",
  color: "#ffffff",
});

const CollapseIcon = styled("span")({
  display: "flex",
  alignItems: "center",
  color: "#a7b2ba",
  fontSize: "20px",
});

const SearchWrapper = styled("div")({
  padding: "0 8px 8px",
  flexShrink: 0,
});

const SearchBox = styled("div")({
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "32px",
  minHeight: "32px",
  gap: "16px",
  padding: "4px 8px",
  border: "1px solid #e2e7ec",
  borderRadius: "8px",
  backgroundColor: "#f5f8fa",
  opacity: 1,
  boxSizing: "border-box",
  "&:focus-within": {
    borderColor: "#a7b2ba",
  },
});

const SearchInputEl = styled("input")({
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: "14px",
  color: "#1e3b44",
  backgroundColor: "transparent",
  lineHeight: 1.4,
  "&::placeholder": {
    color: "#a7b2ba",
  },
});

const FieldsList = styled("div")({
  flex: 1,
  overflowY: "auto",
  padding: "0 8px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  paddingBottom: "8px",
  scrollbarWidth: "thin",
  scrollbarColor: "#dde4e9 transparent",
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#dde4e9",
    borderRadius: "999px",
  },
});

const FieldItem = styled("article", {
  shouldForwardProp: (p) => p !== "isSelected",
})(({ isSelected }) => ({
  padding: "8px 10px",
  borderRadius: "10px",
  backgroundColor: isSelected ? "#e6efe7" : "#ffffff",
  border: `1px solid ${isSelected ? "#d2ded2" : "#edf1f3"}`,
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "8px",
  cursor: "pointer",
  transition: "background 0.15s, border-color 0.15s",
  "&:hover": {
    backgroundColor: isSelected ? "#dbe8dc" : "#f8fafb",
    borderColor: isSelected ? "#b8cfb9" : "#dae2e7",
  },
}));

const FieldItemInfo = styled("div")({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "3px",
});

const FarmerNameLine = styled("span")({
  ...dashboardText,
  fontSize: "13px",
  fontWeight: 600,
  color: "#1e2b32",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ChildGroup = styled("div")({
  paddingLeft: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "1px",
});

const ChildLine = styled("span")({
  ...dashboardText,
  fontSize: "12px",
  color: "#4b5962",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ViewAllBtn = styled("div")({
  ...dashboardText,
  padding: "10px 8px 14px",
  textAlign: "center",
  color: "#6e8391",
  cursor: "pointer",
  flexShrink: 0,
  "&:hover": { textDecoration: "underline" },
});

/* ─────────────────────────────────────────────────────────────────── */
/* Styled: Right form panel                                            */
/* ─────────────────────────────────────────────────────────────────── */

const FormPanel = styled("form")({
  flex: 1,
  minWidth: 0,
  overflowY: "auto",
  padding: "0 20px 14px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  backgroundColor: "#ffffff",
  scrollbarWidth: "thin",
  scrollbarColor: "#dde4e9 transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#dde4e9",
    borderRadius: "999px",
  },
  "& .select": {
    height: "36px",
    borderRadius: "12px",
    backgroundColor: "#eaf1f4",
    color: "#23404c",
    border: "1px solid transparent",
    transition: "border-color 0.2s, background-color 0.2s",
  },
  "& .select .MuiSelect-select": {
    paddingTop: "4px",
    paddingBottom: "4px",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "0.25px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .select .MuiSelect-select > div": {
    minWidth: 0,
    width: "100%",
  },
  "& .common-action-control .select .MuiSelect-select .MuiStack-root": {
    width: "100%",
    minWidth: 0,
  },
  "& .common-action-control .select .text-truncate": {
    display: "block",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .select .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .select:hover": {
    backgroundColor: "#e4edf2",
    borderColor: "#d5e0e7",
  },
  "& .select.Mui-focused": {
    backgroundColor: "#ffffff",
    borderColor: "#7a96a5",
  },
  "& .root .MuiOutlinedInput-root": {
    minHeight: "36px",
    borderRadius: "12px",
    backgroundColor: "#eaf1f4",
    border: "1px solid transparent",
  },
  "& .root .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .root .MuiOutlinedInput-root:hover": {
    backgroundColor: "#e4edf2",
    borderColor: "#d5e0e7",
  },
  "& .root .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: "#ffffff",
    borderColor: "#7a96a5",
  },
  "& .root .MuiInputBase-input": {
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  "& .helper, & .MuiFormHelperText-root": {
    marginLeft: "4px",
  },
  "& .action-modal-card": {
    boxShadow: "none",
    padding: 0,
    border: "none",
  },
  "& .action-details-quantity-input": {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
  },
  "& .action-details-quantity-input.quantity-input-baseline": {
    alignItems: "baseline",
  },
  "& .action-details-column-multiple": {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  "& .action-details-box": {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  "& .action-details-box > div > p:first-of-type": {
    ...dashboardText,
    fontWeight: 500,
    fontSize: "18px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: "#1e2b32",
    marginBottom: "6px",
  },
  "& .action-modal-card .accordion-summary .accordion-title": {
    fontSize: "13px",
    color: "#61747f",
    fontWeight: 600,
  },
  "& .action-modal-card .accordion-summary": {
    marginBottom: "4px",
  },
});

const FormCard = styled("div")({
  padding: "16px",
  borderRadius: "16px",
  border: "1px solid #e2e7ec",
  backgroundColor: "#fafcfd",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const CardTitleRow = styled("div")({
  display: "flex",
  alignItems: "baseline",
  gap: "6px",
});

const CardTitle = styled("span")({
  ...dashboardText,
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "20px",
  letterSpacing: "0.1px",
  color: "#1e2b32",
});

/* Field labels inside cards */
const FieldLabel = styled("div")({
  ...dashboardText,
  fontWeight: 500,
  color: "#5f6f78",
  marginBottom: "4px",
});

/* Two-column grid for stress + enemy */
const TwoColGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  "@media (max-width:920px)": {
    gridTemplateColumns: "1fr",
  },
});

/* Status + dates row */
const StatusDateRow = styled("div")({
  display: "grid",
  gridTemplateColumns: "220px 1fr 1fr",
  gap: "8px",
  alignItems: "start",
  "@media (max-width:920px)": {
    gridTemplateColumns: "1fr",
  },
});

const FormRow = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  minWidth: 0,
});

const ActionKindSectionRow = styled("div")({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 360px)",
  gap: "12px",
  alignItems: "start",
  "@media (max-width:1100px)": {
    gridTemplateColumns: "1fr",
  },
});

const ActionKindSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  minWidth: 0,
});

const ActionKindInputs = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  minWidth: 0,
  "& > *": {
    width: "100%",
    minWidth: 0,
  },
});

const CauseTypeSelectField = () => {
  const { t } = useTranslation();
  const { stressTypes } = useStressTypes();

  const options = useMemo(
    () =>
      stressTypes?.map(({ name, shortName }) => ({
        value: name,
        label: shortName,
      })) || [],
    [stressTypes],
  );

  return (
    <FormSelectInput
      name="stressType"
      className="common-action-control"
      options={options}
      placeholder={t("ActionModal.Cause")}
    />
  );
};

const ActionTypeSelectField = () => {
  const { t } = useTranslation();
  const { actionTypes } = useActionChoices();

  const options = useMemo(
    () =>
      Object.values(actionTypes).map(({ id }) => ({
        value: id,
        label: t("ActionType.Action", { count: id }),
      })),
    [actionTypes, t],
  );

  return (
    <FormSelectInput
      name="actionTypeId"
      className="common-action-control"
      options={options}
      placeholder={t("ActionModal.ActionType")}
    />
  );
};

const ActionOperationInput = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  const { getActionById } = useActionChoices();

  const selectedTask = getActionById(values?.taskId);
  if (!values?.taskId || !selectedTask) {
    return (
      <FormSelectInput
        name="__operationTypePlaceholder"
        className="common-action-control"
        options={[]}
        placeholder={t("ActionModal.OperationTechnique")}
        isDisabled
      />
    );
  }

  return <OperationInput isViewMode={false} />;
};

const StatusAndDatesFields = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  const { isBulk } = useActionModalGetContext();

  const isPerformed = values.status === ACTION_STATUS.performed;
  const fromFieldName = isPerformed
    ? "datetimePerformed.from"
    : "dateRange.from";
  const toFieldName = isPerformed ? "datetimePerformed.to" : "dateRange.to";
  const dateFormatValue = isPerformed
    ? (value) => moment(value).toISOString()
    : "YYYY-MM-DD";
  const statusOptions = [
    {
      value: ACTION_STATUS.suggested,
      label: t("ActionModal.SuggestedAction"),
    },
    // TODO: Re-enable when bulk performed create endpoint is available.
    ...(!isBulk
      ? [
          {
            value: ACTION_STATUS.performed,
            label: t("ActionModal.PerformedAction"),
          },
        ]
      : []),
  ];

  return (
    <StatusDateRow>
      <FormRow>
        <FieldLabel>{t("ActionModal.Status")}</FieldLabel>
        <FormSelectInput
          name="status"
          className="common-action-control"
          options={statusOptions}
          shouldHaveErrorBehaviorAfterSubmit
        />
      </FormRow>
      <FormRow>
        <FieldLabel>{t("Components.DateField.From")}</FieldLabel>
        <FormDateInput
          name={fromFieldName}
          className="common-action-control"
          placeholder={t("Components.DateField.From")}
          shouldHaveErrorBehaviorAfterSubmit
          formatValue={dateFormatValue}
          maxDate={isPerformed ? moment().endOf("day") : undefined}
        />
      </FormRow>
      <FormRow>
        <FieldLabel>{t("Components.DateField.To")}</FieldLabel>
        <FormDateInput
          name={toFieldName}
          className="common-action-control"
          placeholder={t("Components.DateField.To")}
          shouldHaveErrorBehaviorAfterSubmit
          formatValue={dateFormatValue}
          maxDate={isPerformed ? moment().endOf("day") : undefined}
        />
      </FormRow>
    </StatusDateRow>
  );
};

/* ─────────────────────────────────────────────────────────────────── */
/* Main component                                                      */
/* ─────────────────────────────────────────────────────────────────── */

const FIELDS_PREVIEW_LIMIT = 5;
const isFieldBioCrop = (field) =>
  Boolean(field?.isBioCrop ?? field?.is_bio_crop);

const CommonActionReadyContent = ({
  actionAlerts,
  isActionAlertsError = false,
  isActionAlertsLoading = false,
  selectedFields,
  onClose,
  onActionSaved = () => {},
}) => {
  const { t } = useTranslation();
  const { actions } = useActionChoices();
  const { action } = useActionModalGetContext();
  const { setAction, setFieldInfo } = useActionModalSetContext();

  /* All fields selected by default */
  const [selectedFieldIds, setSelectedFieldIds] = useState(
    () => new Set(selectedFields.map((f) => f.id)),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);

  /* Build the active fields list for formik from selection state */
  const activeSelectedFields = useMemo(
    () => selectedFields.filter((f) => selectedFieldIds.has(f.id)),
    [selectedFields, selectedFieldIds],
  );
  const areActiveSelectedFieldsAllBio = useMemo(
    () =>
      activeSelectedFields.length > 0 &&
      activeSelectedFields.every(isFieldBioCrop),
    [activeSelectedFields],
  );

  const { formik, isLoading: isCallLoading } = useActionFormikSetup({
    selectedFields: activeSelectedFields.length
      ? activeSelectedFields
      : selectedFields,
    onClose,
    onActionSaved,
  });

  /* Keep formik fieldIds in sync when selection changes */
  useEffect(() => {
    const ids = Array.from(selectedFieldIds);
    if (ids.length === 1) {
      formik.setFieldValue("fieldId", ids[0]);
      formik.setFieldValue("fieldIds", undefined);
    } else if (ids.length > 1) {
      formik.setFieldValue("fieldIds", ids);
      formik.setFieldValue("fieldId", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldIds]);

  useEffect(() => {
    const firstActiveField = activeSelectedFields[0];
    if (!firstActiveField) return;

    setFieldInfo((previousFieldInfo) => {
      const nextVarietyRead =
        firstActiveField.varietyRead || previousFieldInfo.varietyRead;
      if (
        previousFieldInfo.isBioCrop === areActiveSelectedFieldsAllBio &&
        previousFieldInfo.varietyRead === nextVarietyRead
      ) {
        return previousFieldInfo;
      }

      return {
        ...previousFieldInfo,
        varietyRead: nextVarietyRead,
        isBioCrop: areActiveSelectedFieldsAllBio,
      };
    });
  }, [activeSelectedFields, areActiveSelectedFieldsAllBio, setFieldInfo]);

  /* Keep action status in context */
  useEffect(() => {
    if (typeof setAction === "function") {
      setAction((prev) => ({ ...prev, status: formik.values?.status }));
    }
  }, [formik.values?.status, setAction]);

  const shouldShowActionDetails = useMemo(() => {
    const otherTaskId = actions.other?.taskId;
    return (
      formik.values?.taskId &&
      (formik.values.taskId !== otherTaskId ||
        (formik.values.taskId === otherTaskId &&
          formik.values.otherAction?.operationTypeId))
    );
  }, [
    actions.other?.taskId,
    formik.values.taskId,
    formik.values.otherAction?.operationTypeId,
  ]);

  /* Field toggle */
  const toggleField = (fieldId) => {
    setSelectedFieldIds((prev) => {
      if (prev.size === 1 && prev.has(fieldId)) return prev; // keep at least 1
      const next = new Set(prev);
      next.has(fieldId) ? next.delete(fieldId) : next.add(fieldId);
      return next;
    });
  };

  /* Search filter */
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return selectedFields;
    const q = searchQuery.toLowerCase();
    return selectedFields.filter((f) =>
      (f.name || "").toLowerCase().includes(q),
    );
  }, [selectedFields, searchQuery]);

  const displayedFields = useMemo(
    () =>
      showAll ? filteredFields : filteredFields.slice(0, FIELDS_PREVIEW_LIMIT),
    [filteredFields, showAll],
  );

  const hasMore = !showAll && filteredFields.length > FIELDS_PREVIEW_LIMIT;

  /* Field display helpers */
  const getFarmerName = (field) => {
    const farmer = field?.farmerDetails?.[0];
    if (!farmer) return null;
    return (
      [farmer.firstName, farmer.lastName]
        .map((v) => v?.trim())
        .filter(Boolean)
        .join(" ") || null
    );
  };

  const getCropName = (field) =>
    varietyFullName(field, { startFrom: "crop", splitter: " " });

  return (
    <FormikProvider value={formik}>
      <>
        {/* ── Header ── */}
        <ModalHeader>
          <ModalTitle>{t("ActionModal.Mode.NewAction")}</ModalTitle>
          <ModalHeaderActions>
            <SubmitBtn
              variant="filled"
              corners="square"
              isLoading={isCallLoading}
              isDisabled={isCallLoading}
              onClick={formik.submitForm}
              type="button"
            >
              {t("ActionModal.SubmitButton")}
            </SubmitBtn>
            <CloseBtn type="button" onClick={onClose}>
              <CloseRounded style={{ fontSize: 18 }} />
            </CloseBtn>
          </ModalHeaderActions>
        </ModalHeader>

        {/* ── Body ── */}
        <ModalBody>
          {/* ── Left: fields panel ── */}
          <FieldsPanel>
            <FieldsPanelHeader onClick={() => setIsPanelOpen((v) => !v)}>
              <FieldsPanelTitleGroup>
                <FieldsPanelIcon />
                <FieldsPanelTitle>
                  {t("Global.Field", { count: selectedFields.length })}
                </FieldsPanelTitle>
                <FieldsCountBadge>{selectedFields.length}</FieldsCountBadge>
              </FieldsPanelTitleGroup>
              <CollapseIcon>
                {isPanelOpen ? (
                  <KeyboardArrowUpRounded fontSize="inherit" />
                ) : (
                  <KeyboardArrowDownRounded fontSize="inherit" />
                )}
              </CollapseIcon>
            </FieldsPanelHeader>

            {isPanelOpen && (
              <>
                <SearchWrapper>
                  <SearchBox>
                    <SearchOutlined
                      style={{ fontSize: 15, color: "#a7b2ba" }}
                    />
                    <SearchInputEl
                      placeholder={t(
                        "ActionModal.CommonModal.FieldsPanel.Search",
                      )}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </SearchBox>
                </SearchWrapper>

                <FieldsList>
                  {displayedFields.map((field) => {
                    const isSelected = selectedFieldIds.has(field.id);
                    const farmerName = getFarmerName(field);
                    const cropName = getCropName(field);
                    return (
                      <FieldItem
                        key={field.id}
                        isSelected={isSelected}
                        onClick={() => toggleField(field.id)}
                      >
                        <FieldItemInfo>
                          {farmerName && (
                            <FarmerNameLine>{farmerName}</FarmerNameLine>
                          )}
                          <ChildGroup>
                            <ChildLine title={field.name}>
                              {field.name}
                            </ChildLine>
                            {cropName && <ChildLine>{cropName}</ChildLine>}
                          </ChildGroup>
                        </FieldItemInfo>
                      </FieldItem>
                    );
                  })}
                </FieldsList>

                {hasMore && (
                  <ViewAllBtn onClick={() => setShowAll(true)}>
                    {t("ActionModal.CommonModal.FieldsPanel.ViewAll")}
                  </ViewAllBtn>
                )}
              </>
            )}
          </FieldsPanel>

          {/* ── Right: form ── */}
          <FormPanel onSubmit={formik.handleSubmit}>
            {/* Card 1: Stress / Cause */}
            <FormCard>
              <CardTitleRow>
                <CardTitle>
                  {t("ActionModal.CommonModal.StressInfoTitle")}
                </CardTitle>
              </CardTitleRow>

              <StatusDateRow>
                <div style={{ gridColumn: "1 / 3" }}>
                  <FieldLabel>{t("ActionModal.Cause")}</FieldLabel>
                  <CauseTypeSelectField />
                </div>
              </StatusDateRow>

              <TwoColGrid>
                <div>
                  <FieldLabel>{t("ActionModal.Stress")}</FieldLabel>
                  <StressSelectInput isViewMode={false} />
                </div>
                <div>
                  <FieldLabel>{t("ActionModal.Enemy")}</FieldLabel>
                  <EnemySelectInput isViewMode={false} isNewVersion />
                </div>
              </TwoColGrid>
            </FormCard>

            {/* Card 2: Action info */}
            <FormCard>
              <CardTitleRow>
                <CardTitle>{t("ActionModal.Mode.ViewAction")}</CardTitle>
              </CardTitleRow>

              <StatusAndDatesFields />

              <FormRow>
                <FieldLabel>{t("ActionModal.ActionType")}</FieldLabel>
                <ActionTypeSelectField />
              </FormRow>

              <ActionKindSection>
                <FieldLabel>{t("ActionModal.ActionKind")}</FieldLabel>
                <ActionKindSectionRow>
                  <ActionKindInputs>
                    <TaskSelectInput
                      isViewMode={false}
                      selectedField={activeSelectedFields[0]}
                      isBioCrop={areActiveSelectedFieldsAllBio}
                    />
                    <ActionOperationInput />
                    <OtherOperationVariant isViewMode={false} />
                  </ActionKindInputs>

                  <ActionAlertsStrip
                    actionAlerts={actionAlerts}
                    isError={isActionAlertsError}
                    isLoading={isActionAlertsLoading}
                  />
                </ActionKindSectionRow>
              </ActionKindSection>
            </FormCard>

            {/* Card 3: Task-specific details */}
            {shouldShowActionDetails && (
              <FormCard>
                <ActionDetails
                  viewMode={ACTION_VIEW_MODE.new}
                  actionStatus={action.status}
                  isNewVersion
                />
              </FormCard>
            )}
          </FormPanel>
        </ModalBody>
      </>
    </FormikProvider>
  );
};

export default CommonActionReadyContent;
